import { Create, RemindersRecord, RemindersResponse, StoresRecord, Update } from "@/pocketbase-types"
import { defineStore, acceptHMRUpdate } from "pinia"
import { useListStore } from "./list"
import { sortBy } from "@/utils"

export type ReminderProps = RemindersResponse<{
  store?: StoresRecord
}>

const requestOptions = {
  expand: "store"
}

export const useReminderStore = defineStore("reminder", {
  state: () => ({
    reminders: [] as ReminderProps[]
  }),
  getters: {
    onList: ({ reminders }) => {
      const listStore = useListStore()
      return reminders.filter(r => r.list === listStore.current?.id && !r.inCart)
    },
    inCart: ({ reminders }) => {
      const listStore = useListStore()
      return reminders.filter(r => r.list === listStore.current?.id && r.inCart)
    },
    noStore(): ReminderProps[] {
      return this.onList.filter(r => r.store == "")
    }
  },
  actions: {
    async refresh() {
      this.reminders = await this.pb.collection("reminders").getFullList<ReminderProps>({
        ...requestOptions,
        sort: "+createdAt"
      })
    },
    async create(props: Omit<Create<"reminders">, "household">) {
      const { id } = await this.pb
        .collection("reminders")
        .create({ ...props, household: this.pb.authStore.record?.household })
      const newListItem = await this.pb.collection("reminders").getOne<ReminderProps>(id, requestOptions)
      this.reminders.push(newListItem)
      this.reminders.sort(sortBy(r => r.createdAt))
    },
    async update(reminder: RemindersRecord, props: Update<"reminders">) {
      await this.pb.collection("reminders").update(reminder.id, props)

      const idx = this.reminders.findIndex(r => r.id === reminder.id)
      if (idx > -1) {
        const newReminder = await this.pb.collection("reminders").getOne<ReminderProps>(reminder.id, requestOptions)
        this.reminders.splice(idx, 1, newReminder)
      } else {
        // This should never happen
        await this.refresh()
      }
    },
    async delete(reminder: RemindersRecord, delay = false) {
      await this.pb.collection("reminders").delete(reminder.id)

      const remove = () => {
        const idx = this.reminders.findIndex(r => r.id === reminder.id)
        if (idx > -1) {
          this.reminders.splice(idx, 1)
        }
      }

      if (delay) {
        setTimeout(() => remove(), 500)
      } else {
        remove()
      }
    },
    byStore(id: string) {
      return this.reminders.filter(r => r.store == id)
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReminderStore, import.meta.hot))
}
